import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { FaPlus, FaEdit, FaTrash, FaSave, FaArrowUp, FaArrowDown, FaTimes, FaImage } from 'react-icons/fa';

const SponsorBannerManager = () => {
  const [banners, setBanners] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    description_color: '#fbbf24',
    button1_text: '',
    button1_link: '',
    link: '',
    orderIndex: 0,
    isActive: true,
  });
  const [imageFile, setImageFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchBanners = useCallback(async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get('/api/sponsor-banners/admin', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBanners(response.data);
    } catch (error) {
      console.error('Error fetching sponsor banners:', error);
    }
  }, []);

  useEffect(() => {
    fetchBanners();
  }, [fetchBanners]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name) {
      alert('Sponsor adı zorunludur');
      return;
    }

    if (!imageFile && !editingBanner) {
      alert('Görsel zorunludur');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('adminToken');
      const formDataToSend = new FormData();
      
      formDataToSend.append('name', formData.name);
      formDataToSend.append('description', formData.description || '');
      formDataToSend.append('description_color', formData.description_color || '#fbbf24');
      formDataToSend.append('button1_text', formData.button1_text || '');
      formDataToSend.append('button1_link', formData.button1_link || '');
      formDataToSend.append('link', formData.link || '');
      formDataToSend.append('orderIndex', formData.orderIndex || 0);
      formDataToSend.append('isActive', formData.isActive);

      if (imageFile) {
        formDataToSend.append('image', imageFile);
      } else if (editingBanner && editingBanner.imageUrl) {
        formDataToSend.append('imageUrl', editingBanner.imageUrl);
      }

      if (editingBanner) {
        await axios.put(`/api/sponsor-banners/${editingBanner.id}`, formDataToSend, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
      } else {
        await axios.post('/api/sponsor-banners', formDataToSend, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
      }

      fetchBanners();
      resetForm();
      alert(editingBanner ? 'Banner kartı güncellendi' : 'Banner kartı eklendi');
    } catch (error) {
      console.error('Error saving sponsor banner:', error);
      alert(error.response?.data?.error || 'Banner kartı kaydedilirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      description_color: '#fbbf24',
      button1_text: '',
      button1_link: '',
      link: '',
      orderIndex: 0,
      isActive: true,
    });
    setImageFile(null);
    setPreview(null);
    setEditingBanner(null);
    setShowForm(false);
  };

  const handleEdit = (banner) => {
    setEditingBanner(banner);
    setFormData({
      name: banner.name || '',
      description: banner.description || '',
      description_color: banner.description_color || '#fbbf24',
      button1_text: banner.button1_text || banner.button1Text || '',
      button1_link: banner.button1_link || banner.button1Link || '',
      link: banner.link || '',
      orderIndex: banner.orderIndex || banner.order_index || 0,
      isActive: banner.isActive !== undefined ? banner.isActive : (banner.is_active === 1 || banner.is_active === true),
    });
    setPreview(banner.imageUrl || banner.image_url);
    setImageFile(null);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu banner kartını silmek istediğinize emin misiniz?')) {
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(`/api/sponsor-banners/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchBanners();
      alert('Banner kartı silindi');
    } catch (error) {
      console.error('Error deleting sponsor banner:', error);
      alert('Banner kartı silinirken bir hata oluştu');
    }
  };

  const handleMove = async (id, direction) => {
    try {
      const token = localStorage.getItem('adminToken');
      await axios.put(`/api/sponsor-banners/${id}/move`, { direction }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchBanners();
    } catch (error) {
      console.error('Error moving banner:', error);
      alert('Banner taşınırken bir hata oluştu');
    }
  };

  return (
    <div className="bg-gradient-card rounded-xl p-6 border border-gold/30">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gold">Sponsor Banner Kart Yönetimi</h2>
        <button
          onClick={() => setShowForm(!showForm)}
          className="bg-gradient-to-r from-gold to-yellow-500 hover:from-yellow-500 hover:to-gold text-navy-900 font-bold px-4 py-2 rounded-lg transition-all duration-300 shadow-gold"
        >
          {showForm ? <><FaTimes className="inline-block mr-2" /> İptal</> : <><FaPlus className="inline-block mr-2" /> Yeni Banner Kart</>}
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="bg-navy-700 p-6 rounded-lg mb-6 space-y-4">
          <div>
            <label className="block text-white mb-2">Sponsor Adı *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 bg-navy-600 border border-gold/30 rounded text-white"
              placeholder="Örn: DBbet"
              required
            />
          </div>

          <div>
            <label className="block text-white mb-2">Açıklama</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 bg-navy-600 border border-gold/30 rounded text-white"
              placeholder="Her satır bir açıklama satırı olacak şekilde yazın"
              rows="3"
            />
          </div>

          <div>
            <label className="block text-white mb-2">Açıklama Rengi</label>
            <input
              type="color"
              value={formData.description_color}
              onChange={(e) => setFormData({ ...formData, description_color: e.target.value })}
              className="w-full h-10 bg-navy-600 border border-gold/30 rounded cursor-pointer"
            />
          </div>

          <div>
            <label className="block text-white mb-2">Buton Yazısı</label>
            <input
              type="text"
              value={formData.button1_text}
              onChange={(e) => setFormData({ ...formData, button1_text: e.target.value })}
              className="w-full px-3 py-2 bg-navy-600 border border-gold/30 rounded text-white"
              placeholder="Örn: Siteye Git"
            />
          </div>

          <div>
            <label className="block text-white mb-2">Buton Linki</label>
            <input
              type="url"
              value={formData.button1_link}
              onChange={(e) => setFormData({ ...formData, button1_link: e.target.value })}
              className="w-full px-3 py-2 bg-navy-600 border border-gold/30 rounded text-white"
              placeholder="https://example.com"
            />
          </div>

          <div>
            <label className="block text-white mb-2">Genel Link (Opsiyonel)</label>
            <input
              type="url"
              value={formData.link}
              onChange={(e) => setFormData({ ...formData, link: e.target.value })}
              className="w-full px-3 py-2 bg-navy-600 border border-gold/30 rounded text-white"
              placeholder="https://example.com"
            />
            <p className="text-white/60 text-xs mt-1">Kartın tamamına tıklanınca yönlendirilecek link</p>
          </div>

          <div>
            <label className="block text-white mb-2">Banner Görseli *</label>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              className="w-full px-3 py-2 bg-navy-600 border border-gold/30 rounded text-white"
              required={!editingBanner || !editingBanner.imageUrl}
            />
            {(editingBanner && editingBanner.imageUrl && !imageFile) && (
              <div className="mt-2 text-white/60 text-sm flex items-center gap-2">
                <FaImage /> Mevcut görsel: <a href={editingBanner.imageUrl} target="_blank" rel="noopener noreferrer" className="text-gold hover:underline">Görüntüle</a>
              </div>
            )}
            {preview && (
              <div className="mt-4">
                <p className="text-white mb-2">Önizleme:</p>
                <img
                  src={preview}
                  alt="Preview"
                  className="max-w-full h-auto rounded-lg border border-gold/30"
                  style={{ maxHeight: '300px' }}
                />
              </div>
            )}
          </div>

          <div>
            <label className="block text-white mb-2">Sıra</label>
            <input
              type="number"
              value={formData.orderIndex}
              onChange={(e) => setFormData({ ...formData, orderIndex: parseInt(e.target.value) || 0 })}
              className="w-full px-3 py-2 bg-navy-600 border border-gold/30 rounded text-white"
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="bannerIsActive"
              checked={formData.isActive}
              onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              className="w-5 h-5 text-gold rounded focus:ring-gold"
            />
            <label htmlFor="bannerIsActive" className="text-white">Aktif</label>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-green-500 to-blue-500 hover:from-green-600 hover:to-blue-600 text-white font-bold py-3 rounded-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            <FaSave />
            {loading ? 'Kaydediliyor...' : (editingBanner ? 'Değişiklikleri Kaydet' : 'Banner Kart Oluştur')}
          </button>
        </form>
      )}

      <div className="space-y-4">
        {banners.map((banner, index) => (
          <div key={banner.id} className="bg-navy-700 p-4 rounded-lg">
            <div className="flex flex-col md:flex-row gap-4">
              {banner.imageUrl && (
                <div className="flex-shrink-0">
                  <img
                    src={banner.imageUrl || banner.image_url}
                    alt={banner.name || 'Banner'}
                    className="w-full md:w-48 h-auto object-contain rounded bg-white/5"
                  />
                </div>
              )}
              <div className="flex-1">
                <h3 className="text-white font-bold text-lg mb-2">{banner.name || 'Başlıksız Banner'}</h3>
                {banner.description && (
                  <p className="text-white/80 text-sm mb-2 whitespace-pre-line">{banner.description}</p>
                )}
                <div className="flex flex-wrap gap-2 text-sm text-white/60">
                  <span>Sıra: {banner.orderIndex || banner.order_index || 0}</span>
                  <span>•</span>
                  <span>Durum: {banner.isActive !== undefined ? (banner.isActive ? 'Aktif' : 'Pasif') : (banner.is_active === 1 || banner.is_active === true ? 'Aktif' : 'Pasif')}</span>
                  {banner.button1_link && (
                    <>
                      <span>•</span>
                      <span>Buton: {banner.button1_text || banner.button1Text}</span>
                    </>
                  )}
                  {banner.link && (
                    <>
                      <span>•</span>
                      <span>Link: <a href={banner.link} target="_blank" rel="noopener noreferrer" className="text-gold hover:underline">{banner.link}</a></span>
                    </>
                  )}
                </div>
              </div>
              <div className="flex flex-row md:flex-col gap-2">
                <button
                  onClick={() => handleMove(banner.id, 'up')}
                  disabled={index === 0}
                  className="p-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors disabled:opacity-50"
                  title="Yukarı Taşı"
                >
                  <FaArrowUp />
                </button>
                <button
                  onClick={() => handleMove(banner.id, 'down')}
                  disabled={index === banners.length - 1}
                  className="p-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors disabled:opacity-50"
                  title="Aşağı Taşı"
                >
                  <FaArrowDown />
                </button>
                <button
                  onClick={() => handleEdit(banner)}
                  className="p-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors"
                  title="Düzenle"
                >
                  <FaEdit />
                </button>
                <button
                  onClick={() => handleDelete(banner.id)}
                  className="p-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
                  title="Sil"
                >
                  <FaTrash />
                </button>
              </div>
            </div>
          </div>
        ))}
        {banners.length === 0 && (
          <p className="text-white/60 text-center py-4">Henüz banner kart bulunmamaktadır.</p>
        )}
      </div>
    </div>
  );
};

export default SponsorBannerManager;
